import { useEffect, useState } from 'react'
import { IoWarningOutline } from 'react-icons/io5'
import Modal from '../Modal'
import ModalClose from '../Modal/ModalClose'
import ResidentService from '../../api/resident-service'
import { showToast } from '../../utils/toast'
import { IDisableResidentModal } from '../../interfaces/resident'

const DisableResidentModal = ({
  open,
  setIsOpen,
  residentId,
  initialStatus,
  reload,
}: IDisableResidentModal) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isActive, setIsActive] = useState(initialStatus)
  const [reason, setReason] = useState('')

  useEffect(() => {
    setIsActive(initialStatus)
  }, [initialStatus])

  const action = isActive ? 'disable' : 'enable'

const handleConfirm = async () => {
  if (isLoading || !residentId) return

  if (action === 'disable' && !reason.trim()) {
    showToast({
      title: 'Error',
      description: 'Reason is required.',
      status: 'error',
    })
    return
  }

  setIsLoading(true)

  try {
    const response = await ResidentService.disableOrEnableResident(
      String(residentId),
      action,
      { reason }
    )

    if (!response?.success) {
      throw new Error(
        response?.message || `Failed to ${action} resident.`
      )
    }

    showToast({
      title: `Resident ${action === 'enable' ? 'Enabled' : 'Disabled'}`,
      description: response?.message || `Resident ${action}d successfully.`,
      status: 'success',
    })

    setIsActive((prev) => !prev)
    setReason('')
    setIsOpen(false)
    reload()
  } catch (error: any) {
    const backendMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.response?.data?.errors?.[0]?.message ||
      error?.message ||
      `Failed to ${action} resident.`

    showToast({
      title: 'Error',
      description: backendMessage,
      status: 'error',
    })
  } finally {
    setIsLoading(false)
  }
}

  const handleClose = () => {
    setIsOpen(false)
    setReason('')
  }

  return (
    <Modal
      open={open}
      close={handleClose}
      modalClassName="p-4 sm:p-6 flex flex-col w-full max-w-[calc(100vw-32px)] sm:max-w-md"
    >
      <ModalClose onClick={handleClose} />

      <div className="flex flex-col items-center -mt-4 sm:-mt-6 space-y-4">
        <IoWarningOutline
          className={
            isActive
              ? 'text-red-600 text-4xl sm:text-5xl'
              : 'text-green-600 text-4xl sm:text-5xl'
          }
        />

        <h2
          className={`text-xl sm:text-2xl font-semibold text-center ${
            isActive ? 'text-red-600' : 'text-green-600'
          }`}
        >
          {action === 'disable' ? 'Disable' : 'Enable'} Resident
        </h2>

        <p className="text-sm text-gray-600 text-center">
          Are you sure you want to {action} this resident?
        </p>
      </div>

      <div>
        {action === 'disable' && (
          <input
            type="text"
            placeholder="Enter reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="border rounded-lg p-3 w-full mt-4 text-sm sm:text-base"
          />
        )}

        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            className="w-full sm:w-auto px-4 py-2 bg-gray-300 rounded-lg"
            onClick={handleClose}
          >
            Close
          </button>

          <button
            type="button"
            className={`w-full sm:w-auto px-4 py-2 text-white rounded-lg ${
              action === 'disable' ? 'bg-red-600' : 'bg-green-600'
            }`}
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading
              ? action === 'disable'
                ? 'Disabling...'
                : 'Enabling...'
              : action === 'disable'
              ? 'Disable'
              : 'Enable'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default DisableResidentModal